'use client';

/**
 * @fileoverview `DiscoverTasteCard` — rail-top taste summary card for the
 * `/[city]/discover` surface (FRE-911, restyled WEB-664 / DPL-14, DES-77 /
 * WEB-943).
 *
 * WEB-1221: the heading names the card (`Your taste`). Sub-label
 * `YOUR STRONGEST SIGNALS`, envelope chips, `Tune taste`.
 * The ring and percentage are gone. The shipped envelope number
 * (`tasteProfile.tasteMatchPct` / `meta.profileTasteMatchPct`) is the viewer's
 * strongest single category weight, not a match score and not confidence —
 * web must not render it. Tags stay envelope-only (never design fixtures).
 *
 * Layout is one column: text, tags, action. The card root is `role="group"`
 * with `aria-label="Your taste: {tag1}, {tag2}, {tag3}"`.
 *
 * Honest-absence contract (FRE-896): the Connected card still only mounts when
 * `authenticatedEnvelope.tasteProfile.tasteMatchPct` is a finite number.
 * Anonymous viewers and soaking signed-in viewers get nothing. WEB-943 does
 * not change that gate — it stops *displaying* the number.
 *
 * View/Connected split: `DiscoverTasteCardView` is pure props (story- and
 * test-friendly); `DiscoverTasteCard` reads the SHARED discover subscription so
 * it adds no second `/events/discover` request (cold-load single-fetch
 * invariant preserved — `buildDiscoverQueryArgs` cache key is unchanged).
 */
import * as React from 'react';
import Link from 'next/link';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverEventsQuery } from '@loci/api';
import type { DiscoverTasteChip } from '@loci/types';

import { TextIcon } from '@/components/atoms/TextIcon';
import { cn } from '@/components/atoms/primitives/utils';
import { buildAppSurfaceHref, isCrossHostHref } from '@/lib/app-surface-href';
import { useCityContext, useCityIsStaging, useCitySurface } from '@/lib/city-context.client';
import { defaultCityCode } from '@/lib/cross-host-urls';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';

/**
 * DS `.disc-match__tune`: mono, 10px, --accent, wide tracking, right-aligned.
 * 32px tall (the v3.1 taste-toggle link, WEB-1076 / FRE-1379); `.a-hit-floor`
 * grows the hit box to 44×44 on coarse pointers only, so paint stays compact. Hoisted so the same-origin
 * `<Link>` and the cross-host `<a>` (WEB-834) cannot style-drift apart.
 */
const TUNE_LINK_CLASSES = cn(
  'a-hit-floor inline-flex min-h-8 desktop:min-h-0 items-center gap-1 self-end',
  'font-mono text-micro tracking-[0.08em] text-accent-text no-underline',
  'transition-colors hover:text-clt-skyline',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline focus-visible:ring-offset-2 focus-visible:ring-offset-background',
);

/**
 * Human label for a taste chip — prefer the explicit label, fall back to the
 * slug. Never renders an empty string.
 */
function chipText(chip: DiscoverTasteChip): string {
  const label = chip.label?.trim();
  if (label) return label;
  return chip.slug;
}

/**
 * DES-77 group name. Empty chip lists omit the colon so the name is never
 * `Your taste: ` with nothing after it.
 */
function tasteGroupAriaLabel(chips: readonly DiscoverTasteChip[]): string {
  const tags = chips.map(chipText).filter((text) => text.length > 0);
  if (tags.length === 0) return 'Your taste';
  return `Your taste: ${tags.join(', ')}`;
}

export interface DiscoverTasteCardViewProps {
  /** Top taste chips (e.g. indie / jazz / small venue). May be empty. */
  readonly tasteChips: readonly DiscoverTasteChip[];
  /**
   * Href for the `TUNE TASTE →` link (personalization settings). Relative on
   * the app surface, absolute (`https://app.loci.city/…`) on the marketing
   * surfaces — build it with `buildAppSurfaceHref`, never by hand. An absolute
   * value renders a native `<a>`; see the link element below.
   */
  readonly tuneHref: string;
  /** Optional class merged onto the card root. */
  readonly className?: string;
}

/**
 * Pure presentational taste card. Use this in Storybook and unit tests;
 * the Connected `DiscoverTasteCard` wires it to the shared discover query.
 *
 * DES-77 option 3: one column (label, sub-label, tags, action). No ring, no
 * percentage. `role="group"` names the envelope tags for assistive tech.
 */
export function DiscoverTasteCardView({
  tasteChips,
  tuneHref,
  className,
}: DiscoverTasteCardViewProps): React.ReactElement {
  return (
    <section
      data-testid="discover-taste-card"
      role="group"
      aria-label={tasteGroupAriaLabel(tasteChips)}
      className={cn(
        // Column, 8px gap, 10px padding, skyline-washed surface + skyline
        // border. WEB-943 dropped the two-column ring+head row.
        'flex flex-col gap-2 rounded-md p-2.5',
        'border border-clt-skyline bg-clt-skyline-wash',
        className,
      )}
    >
      <div className="min-w-0">
        <div
          data-testid="discover-taste-card-label"
          className="font-display text-sm font-bold text-accent-text"
        >
          Your taste
        </div>
        <div
          data-testid="discover-taste-card-sublabel"
          className="font-mono text-micro uppercase tracking-[0.08em] text-fg-3"
        >
          YOUR STRONGEST SIGNALS
        </div>
      </div>

      {tasteChips.length > 0 ? (
        <ul data-testid="discover-taste-card-chips" className="flex flex-wrap gap-[3px]">
          {tasteChips.map((chip) => (
            <li
              key={chip.slug}
              data-testid={`discover-taste-card-chip-${chip.slug}`}
              className={cn(
                'rounded-[3px] border border-border bg-sunken px-1.5 py-0.5',
                'font-mono text-micro tracking-[0.04em] text-fg-2',
              )}
            >
              {chipText(chip)}
            </li>
          ))}
        </ul>
      ) : null}

      {/*
        WEB-834 — the element type follows the href. On the app surface the href
        is same-origin, so `next/link` keeps the soft navigation and its
        prefetch. On the city surface it is absolute: there is no client-side
        route to push across origins, and a `<Link>` would RSC-prefetch a path
        the proxy 307s cross-host, whose redirected response `connect-src`
        blocks. A native `<a>` makes "no `?_rsc=` request" structural rather
        than a `prefetch={false}` prop a later refactor could quietly drop —
        the same reasoning `EditorialTwinCard` uses post-WEB-822.
      */}
      {isCrossHostHref(tuneHref) ? (
        <a href={tuneHref} data-testid="discover-taste-card-tune" className={TUNE_LINK_CLASSES}>
          Tune taste <TextIcon />
        </a>
      ) : (
        <Link href={tuneHref} data-testid="discover-taste-card-tune" className={TUNE_LINK_CLASSES}>
          Tune taste <TextIcon />
        </Link>
      )}
    </section>
  );
}

export interface DiscoverTasteCardProps {
  /**
   * Active city — drives both the shared subscription's geo anchor and the
   * `Tune taste` settings href. Optional: defaults to the city context
   * (falling back to the registry default), so the rail can mount
   * `<DiscoverTasteCard />` with no props.
   */
  readonly cityCode?: CityCode;
  /** Optional class merged onto the card root. */
  readonly className?: string;
}

/**
 * Connected taste card. Reads the SHARED discover subscription via the
 * canonical {@link buildDiscoverQueryArgs} cache key — structurally identical
 * args dedupe into the single cold-load `/events/discover` request, so this
 * card never triggers a second fetch (cold-load single-fetch invariant).
 *
 * Renders `null` unless the authenticated envelope carries a real
 * `tasteProfile.tasteMatchPct` (gate only — WEB-943 does not render the
 * number):
 *   - anonymous → no `authenticatedEnvelope` → `null`
 *   - signed-in but soaking → `tasteMatchPct` undefined → `null`
 *   - signed-in with signal → render the card
 */
export function DiscoverTasteCard({
  cityCode,
  className,
}: DiscoverTasteCardProps): React.ReactElement | null {
  const city = useCityContext();
  const resolvedCityCode = cityCode ?? city?.code ?? defaultCityCode();
  // WEB-834 — this card is mounted by `DiscoverFilterRail`, which
  // `DiscoverTemplate` renders on BOTH Discover routes: the app surface
  // (`app.loci.city/<city>/discover`) and the marketing-city surface
  // (`<city>.loci.city/discover`). Settings live only on the app host, so the
  // href has to resolve per surface. Staging comes from request context
  // (`x-is-staging`), never a build-time env (WEB-776).
  const surface = useCitySurface();
  const isStaging = useCityIsStaging();

  const { params } = useDiscoverParams();
  const queryArgs = useDiscoverEventArgs(params, resolvedCityCode);
  const { data } = useDiscoverEventsQuery(queryArgs);

  const tasteProfile = data?.authenticatedEnvelope?.tasteProfile;
  const tasteMatchPct = tasteProfile?.tasteMatchPct;

  // Honest absence: no real taste signal → render nothing (no empty
  // placeholder). Anonymous viewers never reach this branch because the
  // envelope is omitted entirely. Guard on a finite number so a drifted
  // `null` / `NaN` / non-numeric payload never mounts the card. WEB-943
  // still uses this field as the gate and never paints it.
  if (
    tasteProfile === undefined ||
    typeof tasteMatchPct !== 'number' ||
    !Number.isFinite(tasteMatchPct)
  ) {
    return null;
  }

  const tuneHref = buildAppSurfaceHref(resolvedCityCode, '/settings/personalization', surface, {
    isStaging,
  });

  return (
    <DiscoverTasteCardView
      tasteChips={tasteProfile.tasteChips}
      tuneHref={tuneHref}
      className={className}
    />
  );
}
